import { Tabs, VSpacer } from "@/components/DesignSystem";
import { getTabBarSx } from '@/components/shared/styles/cardTabBar';
import { Routes } from '@/constants/Routes';
import { useFeatureFlags } from '@/hooks/useFeatureFlag';
import { Container, Tab, useTheme } from "@mui/material";
import { FeatureFlag } from "@shared/enums";
import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from 'react-router-dom';
import AdminsTab from "./AdminsTab";
import UserSync from "./UserSync";

export enum AdminUsersTab {
  Admins = 'admins',
  CognitoSignInSync = 'cognitoSignInSync',
}

const adminUsersTabRoutes: Record<AdminUsersTab, Routes> = {
  [AdminUsersTab.Admins]: Routes.ADMIN_LIST_ADMINS,
  [AdminUsersTab.CognitoSignInSync]: Routes.ADMIN_COGNITO_SIGN_IN_SYNC,
};

const tabLabels = {
  [AdminUsersTab.Admins]: 'admins',
  [AdminUsersTab.CognitoSignInSync]: 'signInSync',
} as const;

const AdminList = ({ tab = AdminUsersTab.Admins }: { tab?: AdminUsersTab }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { isFetched, isFlagOn } = useFeatureFlags();
  const showSignInSync = isFlagOn(FeatureFlag.CognitoSync);
  const isSyncTab = tab === AdminUsersTab.CognitoSignInSync;

  // The flags load after the page, so a reload of the sync address waits for them.
  if (isSyncTab && !isFetched) {
    return null;
  }
  if (isSyncTab && !showSignInSync) {
    return <Navigate replace to={Routes.ADMIN_LIST_ADMINS} />;
  }

  return (
    <>
      {showSignInSync && (
        <Tabs
          onChange={(_, value: AdminUsersTab) => navigate(adminUsersTabRoutes[value])}
          sx={{ ...getTabBarSx(theme), px: 2 }}
          testID="admin-users-tabs"
          value={tab}
        >
          {Object.values(AdminUsersTab).map((value) => (
            <Tab
              data-testid={`admin-users-tab-${value}`}
              key={value}
              label={t(($) => $.userSync.tabs[tabLabels[value]])}
              value={value}
            />
          ))}
        </Tabs>
      )}
      <Container maxWidth="lg">
        {showSignInSync && <VSpacer size="7" />}
        {isSyncTab ? <UserSync /> : <AdminsTab />}
      </Container>
    </>
  );
};

export default AdminList;
